import { isAxiosError } from 'axios'
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useBoolean, useMountedState } from 'react-use'

import authentication, {
  GOOGLE_VALIDATION_MESSAGE_PREFIX
} from 'api/authentication'
import { CompanyListForm } from 'components/authentication/CompanyListForm'
import { validateGoogleNonce } from 'components/authentication/google-oauth'
import BusyOverlay from 'components/common/BusyOverlay'
import { isChooseCompanyAuthResponse } from 'context/auth-context-utils'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { instanceOfError } from 'utils/errors'
import { messageFromResponseError } from 'utils/strings'
import { ms } from 'utils/time/millisecond'

import type { UserAuthCompany } from 'types/api/user/session'

const DEFAULT_ERROR_MESSAGE = 'Unable to authenticate using Google.'

export const GoogleCallback: React.FC = () => {
  const {
    hash: locationHash,
    pathname: locationPathname,
    search: locationSearch
  } = useLocation()
  const navigate = useNavigate()
  const { startSession } = useAuth()
  const [isSubmitting, setIsSubmitting] = useBoolean(false)
  const [accessToken, setAccessToken] = useState('')
  const [nextPathname, setNextPathname] = useState('')
  const [companies, setCompanies] = useState<UserAuthCompany[]>([])
  const isMounted = useMountedState()

  const handleError = (err: unknown) => {
    const axiosErrorMessage = isAxiosError(err)
      ? messageFromResponseError(err, DEFAULT_ERROR_MESSAGE)
      : undefined
    const validationErrorMessage =
      instanceOfError(err) &&
      err.message.startsWith(GOOGLE_VALIDATION_MESSAGE_PREFIX)
        ? err.message
        : DEFAULT_ERROR_MESSAGE

    toast({
      type: 'error',
      text: axiosErrorMessage ?? validationErrorMessage,
      time: ms.seconds(10),
      testKey: 'signin'
    })
  }

  // We don't wrap this function in a try/catch because we expect errors
  // to be caught at the caller
  const handleGoogleLogin = useCallback(
    async (companyName?: string) => {
      setIsSubmitting(true)

      const authResponse = await authentication.signInWithGoogle({
        accessToken,
        companyName
      })

      // Users who are members of multiple companies will first have to choose which company to authenticate with
      if (isChooseCompanyAuthResponse(authResponse)) {
        setIsSubmitting(false)
        setCompanies(authResponse.companies)

        return
      }

      await startSession(authResponse)

      if (nextPathname !== locationPathname) {
        navigate(nextPathname || '/')
      }
    },
    [
      accessToken,
      locationPathname,
      nextPathname,
      navigate,
      setIsSubmitting,
      startSession
    ]
  )

  const handleLogin = async (companyName: string) => {
    try {
      await handleGoogleLogin(companyName)
    } catch (err) {
      setIsSubmitting(false)
      handleError(err)
    }
  }

  const handleBack = () => {
    navigate('/')
  }

  useEffect(() => {
    try {
      const hashParams = new URLSearchParams(locationHash.replace('#', '?'))
      const stateParam = hashParams.get('state') ?? '{}'

      window.history.replaceState(
        // retain complete history state
        window.history.state,
        '',
        // security: omit window.location.hash so that it can't be revisited
        `${locationPathname}${locationSearch}`
      )

      const accessTokenParam = hashParams.get('access_token')

      if (!accessTokenParam) {
        throw new Error('Missing access_token from redirect hash')
      }

      let googleState: { nextPathname?: string; nonce?: string } = {}

      try {
        googleState = JSON.parse(stateParam)
      } catch {
        // Don't throw an error if state is present but can't be parsed.
        // Further validation happens below.
      }

      // throws if invalid
      validateGoogleNonce(googleState.nonce)

      setAccessToken(accessTokenParam)

      // If there's a path to forward to after sign in, grab it from the URL
      if (googleState.nextPathname) {
        setNextPathname(googleState.nextPathname)
      }
    } catch (err) {
      setIsSubmitting(false)
      handleError(err)
      navigate('/')
    }
  }, [
    locationHash,
    locationSearch,
    navigate,
    locationPathname,
    setIsSubmitting
  ])

  useEffect(() => {
    // When the component "mounts", we make an initial request to the Gremlin API
    // to get a Gremlin credential using Google's access token. If the result is
    // a list of companies, we set the state and wait until the user selects a
    // company at which point we will invoke handleGoogleLogin again.
    const handleMount = async () => {
      if (!accessToken || !isMounted()) {
        return
      }

      try {
        await handleGoogleLogin()
      } catch (err) {
        setIsSubmitting(false)
        handleError(err)
        navigate('/')
      }
    }

    handleMount()
  }, [
    accessToken,
    handleGoogleLogin,
    isMounted,
    navigate,
    setIsSubmitting,
    setAccessToken,
    setNextPathname
  ])

  if (companies?.length) {
    return (
      <CompanyListForm
        companies={companies}
        handleBack={handleBack}
        handleCompanySelection={(companyName) => handleLogin(companyName)}
        submitting={isSubmitting}
      />
    )
  }

  return <BusyOverlay shown text="Authorizing" />
}
